<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useFunnelManager } from 'dashboard/composables/kanban/useFunnelManager';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import FollowUpTimeline from './components/funnel/FollowUpTimeline.vue';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import FunnelsAPI from 'dashboard/api/funnels';
import { useMapGetter } from 'dashboard/composables/store';

const props = defineProps({
  funnelId: {
    type: [String, Number],
    default: null,
  },
});

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { funnels, currentFunnel, loadFunnels, selectFunnel, updateFunnel } =
  useFunnelManager();

// Estado local
const isLoading = ref(true);
const isSaving = ref(false);
const aiAutoMoveEnabled = ref(false);
// Modo da IA no kanban: 'off' | 'suggest' (sugere, time aprova) | 'auto' (move sozinha)
const aiMode = ref('off');
const aiConfidenceThreshold = ref(70);
const selectedAgents = ref([]);
// Alerta de lead sem resposta (minutos). 0 = desligado.
const speedToLeadMinutes = ref(0);
// Alerta de lead parado (deal aging). Backend exige boolean === true.
const staleAlertEnabled = ref(false);
const staleMaxDaysInStage = ref(14);
const duplicatePolicy = ref('one_open_per_contact');
const customerDuplicatePolicy = ref('inherit');
// Board: colapsa as várias conversas do mesmo contato num card só.
const boardOneCardPerContact = ref(false);
// Movimentação por FATO: evento objetivo avança o card sem passar pela IA.
// A ordem aqui é a ordem em que aparecem na tela.
const STAGE_EVENT_KEYS = [
  'appointment_scheduled',
  'appointment_done',
  'appointment_no_show',
  'proposal_sent',
  'payment_paid',
];
const stageOnEvent = ref({});
const funnelName = ref('');
const funnelDescription = ref('');

// Estado de etapas
const newStageName = ref('');
const newStageColor = ref('#3B82F6');
const newStageDescription = ref('');
const editingStageKey = ref(null);
const isSavingStage = ref(false);
const deletingStageKey = ref(null);

// SLA da etapa: valor numérico + unidade (armazenado em HORAS no backend).
const newStageSlaValue = ref('');
const newStageSlaUnit = ref('hours'); // 'hours' | 'days'

// Follow-up state
const expandedStageFollowUp = ref(null);

// Inboxes
const allInboxes = useMapGetter('inboxes/getInboxes');

const WHATSAPP_PROVIDERS = [
  'evolution',
  'waha',
  'uazapi',
  'baileys',
  'notificame',
  'whatsapp_cloud',
  'default',
];

const isWhatsAppInbox = inbox => {
  const ct = inbox.channel_type;
  if (ct === 'Channel::Whatsapp') return true;
  if (ct === 'Channel::TwilioSms' && inbox.medium === 'whatsapp') return true;
  if (ct === 'Channel::Api') {
    const provider = (
      inbox.provider ||
      inbox.additional_attributes?.provider ||
      ''
    ).toLowerCase();
    if (WHATSAPP_PROVIDERS.includes(provider)) return true;
    if (inbox.additional_attributes?.channel_identifier === 'whatsapp')
      return true;
  }
  return false;
};

const availableInboxes = computed(() => {
  return (allInboxes.value || []).filter(
    i => isWhatsAppInbox(i) || i.channel_type === 'Channel::WebWidget'
  );
});

// Templates por inbox — o editor do passo usa isso pra restringir o
// seletor de template à caixa de saída escolhida (evita a combinação
// inválida de template de uma inbox indo pela outra).
const followUpTemplatesByInbox = computed(() => {
  const map = {};
  (availableInboxes.value || []).forEach(inbox => {
    map[inbox.id] =
      store.getters['inboxes/getFilteredWhatsAppTemplates'](inbox.id) || [];
  });
  return map;
});

// União de todos os templates de todos os inboxes elegíveis do funil —
// usada só quando o passo não tem uma inbox específica escolhida ("Mesma
// caixa da conversa"), caso em que não dá pra saber a inbox alvo em tempo
// de configuração.
const followUpTemplates = computed(() => {
  const byName = new Map();
  (availableInboxes.value || []).forEach(inbox => {
    const templates =
      store.getters['inboxes/getFilteredWhatsAppTemplates'](inbox.id) || [];
    templates.forEach(tpl => {
      if (tpl?.name && !byName.has(tpl.name)) {
        byName.set(tpl.name, tpl);
      }
    });
  });
  return Array.from(byName.values());
});

const stageColors = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#6366F1',
];

// Computed
const availableAgents = computed(() => {
  return store.getters['agents/getAgents'] || [];
});

const funnelIdToUse = computed(() => {
  return props.funnelId || route.params.funnelId;
});

const orderedStages = computed(() => {
  if (!currentFunnel.value?.stages) return [];
  return Object.entries(currentFunnel.value.stages)
    .map(([key, stage]) => ({ key, ...stage }))
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
});

const isDefaultFunnel = computed(() => {
  return (
    typeof currentFunnel.value?.id === 'string' &&
    currentFunnel.value.id.startsWith('default-')
  );
});

const applySettings = settings => {
  aiAutoMoveEnabled.value = settings.ai_auto_move || false;
  // Compat: funis antigos só tinham o booleano ai_auto_move.
  if (['off', 'suggest', 'auto'].includes(settings.ai_mode)) {
    aiMode.value = settings.ai_mode;
  } else {
    aiMode.value = settings.ai_auto_move ? 'auto' : 'off';
  }
  aiConfidenceThreshold.value = Math.round(
    (settings.ai_confidence_threshold || 0.7) * 100
  );
  selectedAgents.value = settings.agent_ids || [];
  speedToLeadMinutes.value = parseInt(settings.speed_to_lead_minutes, 10) || 0;
  staleAlertEnabled.value = settings.enable_deal_aging === true;
  staleMaxDaysInStage.value =
    parseInt(settings.default_max_days_in_stage, 10) || 14;
  duplicatePolicy.value = settings.duplicate_policy || 'one_open_per_contact';
  customerDuplicatePolicy.value =
    settings.customer_duplicate_policy || 'inherit';
  boardOneCardPerContact.value = settings.board_one_card_per_contact === true;
  const configured = settings.stage_on_event || {};
  stageOnEvent.value = Object.fromEntries(
    STAGE_EVENT_KEYS.map(event => [event, configured[event] || ''])
  );
};

// Modelo único "Lia Full" (2026-07): a movimentação por IA é liberada para
// TODOS os clientes — o gate por versão de LIA (1.4/2.1/3.1) virou legado.
const hasLia14OrHigher = computed(() => true);

// Methods
const loadSettings = async () => {
  if (!currentFunnel.value) return;

  funnelName.value = currentFunnel.value.name || '';
  funnelDescription.value = currentFunnel.value.description || '';

  try {
    if (isDefaultFunnel.value) {
      if (currentFunnel.value.settings) {
        applySettings(currentFunnel.value.settings);
      }
      return;
    }

    const response = await store.dispatch(
      'funnels/show',
      currentFunnel.value.id
    );
    const funnelData = response || currentFunnel.value;

    funnelName.value = funnelData.name || '';
    funnelDescription.value = funnelData.description || '';

    if (funnelData?.settings) {
      applySettings(funnelData.settings);
    }
  } catch {
    funnelName.value = currentFunnel.value.name || '';
    funnelDescription.value = currentFunnel.value.description || '';
    if (currentFunnel.value?.settings) {
      applySettings(currentFunnel.value.settings);
    }
  }
};

const saveSettings = async () => {
  if (!currentFunnel.value) return;

  const normalizedName = funnelName.value.trim();
  if (!normalizedName) {
    useAlert(t('CRM.FUNNELS.ADD.FORM.NAME_REQUIRED'));
    return;
  }

  isSaving.value = true;

  try {
    const updatedSettings = {
      ...currentFunnel.value.settings,
      ai_mode: hasLia14OrHigher.value ? aiMode.value : 'off',
      // Mantém o booleano legado em sincronia (true só no modo automático).
      ai_auto_move: hasLia14OrHigher.value && aiMode.value === 'auto',
      ai_confidence_threshold: aiConfidenceThreshold.value / 100,
      agent_ids: selectedAgents.value,
      speed_to_lead_minutes: Math.min(
        Math.max(parseInt(speedToLeadMinutes.value, 10) || 0, 0),
        1440
      ),
      // Boolean explícito: o job Crm::LeadAlertsJob exige `=== true`
      enable_deal_aging: staleAlertEnabled.value === true,
      default_max_days_in_stage: Math.min(
        Math.max(parseInt(staleMaxDaysInStage.value, 10) || 14, 1),
        365
      ),
      duplicate_policy: duplicatePolicy.value,
      customer_duplicate_policy: customerDuplicatePolicy.value,
      board_one_card_per_contact: boardOneCardPerContact.value === true,
      // Evento sem etapa escolhida não vai no payload: a faixa fica desligada.
      stage_on_event: Object.fromEntries(
        Object.entries(stageOnEvent.value).filter(([, stage]) => stage)
      ),
      updated_at: new Date().toISOString(),
    };

    const updatedFunnel = await updateFunnel(currentFunnel.value.id, {
      name: normalizedName,
      description: funnelDescription.value.trim(),
      settings: updatedSettings,
    });

    currentFunnel.value.name = updatedFunnel.name;
    currentFunnel.value.description = updatedFunnel.description;
    currentFunnel.value.settings = updatedSettings;

    const funnelIndex = funnels.value.findIndex(
      f => f.id === currentFunnel.value.id
    );
    if (funnelIndex !== -1) {
      funnels.value[funnelIndex].settings = updatedSettings;
      funnels.value[funnelIndex].name = updatedFunnel.name;
      funnels.value[funnelIndex].description = updatedFunnel.description;
    }

    useAlert(t('CRM.FUNNEL_SETTINGS.API.SUCCESS_MESSAGE'));

    window.dispatchEvent(
      new CustomEvent('funnel-settings-saved', {
        detail: {
          funnelId: currentFunnel.value.id,
          settings: updatedSettings,
        },
      })
    );
  } catch {
    useAlert(t('CRM.FUNNEL_SETTINGS.API.ERROR_MESSAGE'));
  } finally {
    isSaving.value = false;
  }
};

// SLA da etapa (Task 1): sla_hours é sempre persistido em HORAS no stage
// config. A UI trabalha com número + unidade (horas/dias) e converte nas
// duas pontas — dias -> horas ao salvar (×24), horas -> dias ao abrir
// (só quando o valor é múltiplo exato de 24, senão mostra em horas).
const deriveSlaFormFromStage = stage => {
  const hours = stage?.sla_hours;
  if (hours === undefined || hours === null || hours === '') {
    newStageSlaValue.value = '';
    newStageSlaUnit.value = 'hours';
    return;
  }
  const numericHours = Number(hours);
  if (
    Number.isFinite(numericHours) &&
    numericHours > 0 &&
    numericHours % 24 === 0
  ) {
    newStageSlaValue.value = numericHours / 24;
    newStageSlaUnit.value = 'days';
  } else {
    newStageSlaValue.value = numericHours;
    newStageSlaUnit.value = 'hours';
  }
};

// Sempre retorna a chave sla_hours: número em horas quando preenchido, ou
// `null` quando o campo está vazio — o `null` explícito é o sinal que o
// backend usa pra remover a chave da etapa (em vez de silenciosamente
// preservar o valor antigo).
const slaHoursForPayload = () => {
  const raw = newStageSlaValue.value;
  if (raw === '' || raw === null || raw === undefined) return null;
  const num = Number(raw);
  if (!Number.isFinite(num)) return null;
  return newStageSlaUnit.value === 'days' ? num * 24 : num;
};

// Stage management
const resetStageForm = () => {
  newStageName.value = '';
  newStageColor.value =
    stageColors[(orderedStages.value.length || 0) % stageColors.length];
  newStageDescription.value = '';
  editingStageKey.value = null;
  newStageSlaValue.value = '';
  newStageSlaUnit.value = 'hours';
};

const startEditStage = stage => {
  editingStageKey.value = stage.key;
  newStageName.value = stage.label;
  newStageColor.value = stage.color || '#3B82F6';
  newStageDescription.value = stage.description || '';
  deriveSlaFormFromStage(stage);
};

const cancelEditStage = () => {
  resetStageForm();
};

const addOrUpdateStage = async () => {
  if (!newStageName.value.trim()) return;
  if (!currentFunnel.value?.id || isDefaultFunnel.value) return;

  isSavingStage.value = true;

  try {
    if (editingStageKey.value) {
      await FunnelsAPI.updateStage(
        currentFunnel.value.id,
        editingStageKey.value,
        {
          label: newStageName.value.trim(),
          color: newStageColor.value,
          description: newStageDescription.value.trim(),
          sla_hours: slaHoursForPayload(),
        }
      );
    } else {
      await FunnelsAPI.addStage(currentFunnel.value.id, {
        label: newStageName.value.trim(),
        color: newStageColor.value,
        description: newStageDescription.value.trim(),
        sla_hours: slaHoursForPayload(),
      });
    }

    const response = await store.dispatch(
      'funnels/show',
      currentFunnel.value.id
    );
    if (response?.stages) {
      currentFunnel.value.stages = response.stages;
    }

    useAlert(
      editingStageKey.value
        ? t('CRM.FUNNEL_SETTINGS.STAGES.UPDATED')
        : t('CRM.FUNNEL_SETTINGS.STAGES.ADDED')
    );
    resetStageForm();
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      t('CRM.FUNNEL_SETTINGS.API.ERROR_MESSAGE');
    useAlert(msg);
  } finally {
    isSavingStage.value = false;
  }
};

const deleteStage = async stageKey => {
  if (!currentFunnel.value?.id || isDefaultFunnel.value) return;

  deletingStageKey.value = stageKey;

  try {
    await FunnelsAPI.deleteStage(currentFunnel.value.id, stageKey);

    const response = await store.dispatch(
      'funnels/show',
      currentFunnel.value.id
    );
    if (response?.stages) {
      currentFunnel.value.stages = response.stages;
    }

    useAlert(t('CRM.FUNNEL_SETTINGS.STAGES.DELETED'));

    if (editingStageKey.value === stageKey) {
      resetStageForm();
    }
  } catch (error) {
    const msg =
      error?.response?.data?.error ||
      t('CRM.FUNNEL_SETTINGS.API.ERROR_MESSAGE');
    useAlert(msg);
  } finally {
    deletingStageKey.value = null;
  }
};

const moveStage = async (stageKey, direction) => {
  if (!currentFunnel.value?.id || isDefaultFunnel.value) return;

  const stages = orderedStages.value.map(s => s.key);
  const index = stages.indexOf(stageKey);

  if (
    (direction === 'up' && index === 0) ||
    (direction === 'down' && index === stages.length - 1)
  ) {
    return;
  }

  const swapIndex = direction === 'up' ? index - 1 : index + 1;
  [stages[index], stages[swapIndex]] = [stages[swapIndex], stages[index]];

  try {
    await FunnelsAPI.reorderStages(currentFunnel.value.id, stages);

    const response = await store.dispatch(
      'funnels/show',
      currentFunnel.value.id
    );
    if (response?.stages) {
      currentFunnel.value.stages = response.stages;
    }
  } catch {
    useAlert(t('CRM.FUNNEL_SETTINGS.API.ERROR_MESSAGE'));
  }
};

// Follow-up methods
const toggleStageFollowUp = stageKey => {
  expandedStageFollowUp.value =
    expandedStageFollowUp.value === stageKey ? null : stageKey;
};

const getStageFollowUps = stageKey => {
  return currentFunnel.value?.stages?.[stageKey]?.follow_ups || [];
};

// Mesmo fluxo de persistência do addFollowUpStep/removeFollowUpStep
// originais: reconstrói `stages` com os follow_ups da etapa trocados e
// salva pelo updateFunnel já usado pelo resto da tela. A FollowUpTimeline
// emite o array já pronto (reordenado/editado/pausado/duplicado/removido).
const persistStageSteps = async (stageKey, newSteps) => {
  if (!currentFunnel.value?.id) return;

  const stages = { ...currentFunnel.value.stages };
  if (!stages[stageKey]) return;

  stages[stageKey] = { ...stages[stageKey], follow_ups: newSteps };

  try {
    await updateFunnel(currentFunnel.value.id, { stages });
    currentFunnel.value.stages = stages;
  } catch {
    useAlert(t('CRM.FUNNEL_SETTINGS.API.ERROR_MESSAGE'));
  }
};

const goBack = () => {
  const accountId = store.getters.getCurrentAccountId;
  router.push({
    name: 'manage_funnels',
    params: { accountId },
  });
};

// Lifecycle
onMounted(async () => {
  await store.dispatch('agents/get');
  await store.dispatch('inboxes/get');
  await store.dispatch('liaAgents/fetchEntitlements');
  await loadFunnels();

  if (funnelIdToUse.value) {
    selectFunnel(funnelIdToUse.value);
  } else if (funnels.value.length > 0 && !currentFunnel.value) {
    selectFunnel(funnels.value[0].id);
  }

  await loadSettings();
  isLoading.value = false;
});

watch(
  currentFunnel,
  (newFunnel, oldFunnel) => {
    if (newFunnel && (!oldFunnel || newFunnel.id !== oldFunnel?.id)) {
      loadSettings();
      resetStageForm();
    }
  },
  { immediate: false }
);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('CRM.FUNNEL_SETTINGS.LOADING')"
    :no-records-found="!currentFunnel"
    :no-records-message="$t('CRM.FUNNEL_SETTINGS.NOT_FOUND')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="funnelName || $t('CRM.FUNNEL_SETTINGS.HEADER')"
        :description="
          funnelDescription || $t('CRM.FUNNEL_SETTINGS.DESCRIPTION')
        "
        feature-name="funnels"
      >
        <template #actions>
          <Button
            icon="i-lucide-arrow-left"
            :label="$t('CRM.FUNNEL_SETTINGS.BACK')"
            slate
            faded
            @click="goBack"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="space-y-6 w-full">
        <!-- Identificação do Funil -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
        >
          <div class="flex items-center gap-3 px-5 py-4 border-b border-n-weak">
            <div
              class="w-8 h-8 rounded-lg bg-n-blue-3 flex items-center justify-center"
            >
              <span
                class="i-lucide-text-cursor-input text-n-blue-11 text-base"
              />
            </div>
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('CRM.FUNNELS.ADD.FORM.NAME.LABEL') }}
              </h3>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ $t('CRM.FUNNELS.ADD.FORM.DESCRIPTION.LABEL') }}
              </p>
            </div>
          </div>
          <div class="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
            <woot-input
              v-model="funnelName"
              class="mb-0"
              :label="$t('CRM.FUNNELS.ADD.FORM.NAME.LABEL')"
              :placeholder="$t('CRM.FUNNELS.ADD.FORM.NAME.PLACEHOLDER')"
            />
            <woot-input
              v-model="funnelDescription"
              class="mb-0"
              :label="$t('CRM.FUNNELS.ADD.FORM.DESCRIPTION.LABEL')"
              :placeholder="$t('CRM.FUNNELS.ADD.FORM.DESCRIPTION.PLACEHOLDER')"
            />
          </div>
        </div>

        <!-- Etapas do Funil -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
        >
          <div class="flex items-center gap-3 px-5 py-4 border-b border-n-weak">
            <div
              class="w-8 h-8 rounded-lg bg-n-blue-3 flex items-center justify-center"
            >
              <span class="i-lucide-git-branch-plus text-n-blue-11 text-base" />
            </div>
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('CRM.FUNNEL_SETTINGS.STAGES.HEADER') }}
              </h3>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ $t('CRM.FUNNEL_SETTINGS.STAGES.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="p-5">
            <!-- Lista de etapas existentes -->
            <div class="space-y-1.5 mb-4">
              <div
                v-for="(stage, index) in orderedStages"
                :key="stage.key"
                class="flex items-center gap-3 px-3 py-2.5 rounded-lg group transition-all"
                :class="
                  editingStageKey === stage.key
                    ? 'bg-n-blue-3 ring-1 ring-n-blue-9'
                    : 'bg-n-alpha-1 hover:bg-n-alpha-2'
                "
              >
                <div
                  class="w-3.5 h-3.5 rounded-full flex-shrink-0 ring-2 ring-white/20"
                  :style="{ backgroundColor: stage.color }"
                />
                <span class="flex-1 text-sm text-n-slate-12 font-medium">
                  {{ stage.label }}
                </span>
                <span
                  v-if="stage.description"
                  class="text-xs text-n-slate-10 truncate max-w-40 hidden sm:block"
                >
                  {{ stage.description }}
                </span>
                <div
                  v-if="!isDefaultFunnel"
                  class="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Button
                    v-if="index > 0"
                    v-tooltip.top="$t('CRM.FUNNEL_SETTINGS.STAGES.MOVE_UP')"
                    icon="i-lucide-chevron-up"
                    xs
                    slate
                    faded
                    @click="moveStage(stage.key, 'up')"
                  />
                  <Button
                    v-if="index < orderedStages.length - 1"
                    v-tooltip.top="$t('CRM.FUNNEL_SETTINGS.STAGES.MOVE_DOWN')"
                    icon="i-lucide-chevron-down"
                    xs
                    slate
                    faded
                    @click="moveStage(stage.key, 'down')"
                  />
                  <Button
                    v-tooltip.top="$t('CRM.FUNNEL_SETTINGS.STAGES.EDIT')"
                    icon="i-lucide-pen"
                    xs
                    slate
                    faded
                    :data-testid="`stage-edit-${stage.key}`"
                    @click="startEditStage(stage)"
                  />
                  <Button
                    v-tooltip.top="$t('CRM.FUNNEL_SETTINGS.STAGES.DELETE')"
                    icon="i-lucide-trash-2"
                    xs
                    ruby
                    faded
                    :is-loading="deletingStageKey === stage.key"
                    @click="deleteStage(stage.key)"
                  />
                </div>
              </div>
              <p
                v-if="orderedStages.length === 0"
                class="text-sm text-center text-n-slate-11 py-6"
              >
                {{ $t('CRM.FUNNEL_SETTINGS.STAGES.EMPTY') }}
              </p>
            </div>

            <!-- Formulario de adicionar/editar etapa -->
            <div v-if="!isDefaultFunnel" class="border-t border-n-weak pt-4">
              <h4
                class="text-xs font-semibold text-n-slate-11 mb-2 uppercase tracking-wide"
              >
                {{
                  editingStageKey
                    ? $t('CRM.FUNNEL_SETTINGS.STAGES.EDIT_TITLE')
                    : $t('CRM.FUNNEL_SETTINGS.STAGES.ADD_TITLE')
                }}
              </h4>
              <div class="flex flex-col gap-2">
                <div class="flex gap-2">
                  <woot-input
                    v-model="newStageName"
                    class="flex-1 mb-0"
                    :placeholder="
                      $t('CRM.FUNNEL_SETTINGS.STAGES.NAME_PLACEHOLDER')
                    "
                    @keyup.enter="addOrUpdateStage"
                  />
                  <woot-color-picker v-model="newStageColor" />
                </div>
                <woot-input
                  v-model="newStageDescription"
                  class="w-full mb-0"
                  :placeholder="
                    $t('CRM.FUNNEL_SETTINGS.STAGES.DESCRIPTION_PLACEHOLDER')
                  "
                />
                <div>
                  <label
                    class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11 mb-1.5"
                  >
                    <span class="i-lucide-clock text-xs" />
                    {{ $t('CRM.FUNNEL_SETTINGS.STAGES.SLA_LABEL') }}
                  </label>
                  <div class="flex items-center gap-2">
                    <woot-input
                      v-model="newStageSlaValue"
                      type="number"
                      min="0"
                      class="w-28 mb-0"
                      data-testid="stage-sla-value"
                      :placeholder="
                        $t('CRM.FUNNEL_SETTINGS.STAGES.SLA_PLACEHOLDER')
                      "
                    />
                    <div
                      class="flex rounded-lg border border-n-weak overflow-hidden"
                    >
                      <button
                        type="button"
                        data-testid="stage-sla-unit-hours"
                        :aria-pressed="newStageSlaUnit === 'hours'"
                        class="px-3 py-1.5 text-xs font-medium transition-colors"
                        :class="
                          newStageSlaUnit === 'hours'
                            ? 'bg-n-blue-3 text-n-blue-11'
                            : 'bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-2'
                        "
                        @click="newStageSlaUnit = 'hours'"
                      >
                        {{ $t('CRM.FUNNEL_SETTINGS.STAGES.SLA_UNIT_HOURS') }}
                      </button>
                      <button
                        type="button"
                        data-testid="stage-sla-unit-days"
                        :aria-pressed="newStageSlaUnit === 'days'"
                        class="px-3 py-1.5 text-xs font-medium transition-colors border-l border-n-weak"
                        :class="
                          newStageSlaUnit === 'days'
                            ? 'bg-n-blue-3 text-n-blue-11'
                            : 'bg-n-solid-2 text-n-slate-11 hover:bg-n-alpha-2'
                        "
                        @click="newStageSlaUnit = 'days'"
                      >
                        {{ $t('CRM.FUNNEL_SETTINGS.STAGES.SLA_UNIT_DAYS') }}
                      </button>
                    </div>
                  </div>
                  <p class="text-xs text-n-slate-11 mt-1.5">
                    {{ $t('CRM.FUNNEL_SETTINGS.STAGES.SLA_HINT') }}
                  </p>
                </div>
                <div class="flex gap-2 justify-end">
                  <Button
                    v-if="editingStageKey"
                    :label="$t('CRM.FUNNEL_SETTINGS.STAGES.CANCEL')"
                    slate
                    faded
                    xs
                    @click="cancelEditStage"
                  />
                  <Button
                    :icon="editingStageKey ? 'i-lucide-check' : 'i-lucide-plus'"
                    :label="
                      editingStageKey
                        ? $t('CRM.FUNNEL_SETTINGS.STAGES.UPDATE')
                        : $t('CRM.FUNNEL_SETTINGS.STAGES.ADD')
                    "
                    xs
                    data-testid="stage-save"
                    :is-loading="isSavingStage"
                    @click="addOrUpdateStage"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- AI Auto-Move + Agentes side by side -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
          <!-- AI Auto-Move -->
          <div
            class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
          >
            <div
              class="flex items-center gap-3 px-5 py-4 border-b border-n-weak"
            >
              <div
                class="w-8 h-8 rounded-lg bg-n-violet-3 flex items-center justify-center"
              >
                <span class="i-lucide-sparkles text-n-violet-11 text-base" />
              </div>
              <div>
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ $t('CRM.FUNNEL_SETTINGS.SECTIONS.AI_AUTO_MOVE') }}
                </h3>
                <p class="text-xs text-n-slate-11 mt-0.5">
                  {{ $t('CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.DESCRIPTION') }}
                </p>
              </div>
            </div>
            <div class="p-5 space-y-4">
              <div
                v-if="!hasLia14OrHigher"
                class="flex items-center gap-3 p-3 rounded-lg bg-n-amber-3 border border-n-amber-6"
              >
                <span
                  class="i-lucide-lock text-n-amber-11 text-base flex-shrink-0"
                />
                <p class="text-sm text-n-amber-11">
                  {{ $t('CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.REQUIRES_LIA_14') }}
                </p>
              </div>
              <div
                class="space-y-2"
                :class="
                  hasLia14OrHigher ? '' : 'opacity-50 pointer-events-none'
                "
              >
                <label
                  v-for="opt in [
                    {
                      value: 'off',
                      label: $t('CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.MODE_OFF'),
                      hint: '',
                    },
                    {
                      value: 'suggest',
                      label: $t(
                        'CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.MODE_SUGGEST'
                      ),
                      hint: $t(
                        'CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.MODE_SUGGEST_HINT'
                      ),
                    },
                    {
                      value: 'auto',
                      label: $t('CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.MODE_AUTO'),
                      hint: $t(
                        'CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.MODE_AUTO_HINT'
                      ),
                    },
                  ]"
                  :key="opt.value"
                  class="flex items-start gap-3 p-3 rounded-lg border cursor-pointer"
                  :class="
                    aiMode === opt.value
                      ? 'border-n-brand bg-n-brand/5'
                      : 'border-n-weak'
                  "
                >
                  <input
                    v-model="aiMode"
                    :value="opt.value"
                    type="radio"
                    :disabled="!hasLia14OrHigher"
                    class="mt-0.5 w-4 h-4 text-n-brand focus:ring-n-brand"
                  />
                  <span>
                    <span class="block text-sm text-n-slate-12">
                      {{ opt.label }}
                    </span>
                    <span v-if="opt.hint" class="block text-xs text-n-slate-11">
                      {{ opt.hint }}
                    </span>
                  </span>
                </label>
              </div>
              <div
                v-if="aiMode !== 'off' && hasLia14OrHigher"
                class="ml-7 space-y-2"
              >
                <label class="block text-xs font-medium text-n-slate-11">
                  {{
                    $t('CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.CONFIDENCE_LABEL', {
                      value: aiConfidenceThreshold,
                    })
                  }}
                </label>
                <input
                  v-model.number="aiConfidenceThreshold"
                  type="range"
                  min="50"
                  max="95"
                  step="5"
                  class="w-full h-2 rounded-lg appearance-none cursor-pointer bg-n-alpha-3"
                />
                <div class="flex justify-between text-xs text-n-slate-10">
                  <span>{{
                    $t('CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.MIN_CONFIDENCE')
                  }}</span>
                  <span>{{
                    $t('CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.CONSERVATIVE')
                  }}</span>
                  <span>{{
                    $t('CRM.FUNNEL_SETTINGS.AI_AUTO_MOVE.MAX_CONFIDENCE')
                  }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Agentes com Acesso -->
          <div
            class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
          >
            <div
              class="flex items-center gap-3 px-5 py-4 border-b border-n-weak"
            >
              <div
                class="w-8 h-8 rounded-lg bg-n-amber-3 flex items-center justify-center"
              >
                <span class="i-lucide-users text-n-amber-11 text-base" />
              </div>
              <div>
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ $t('CRM.FUNNEL_SETTINGS.SECTIONS.AGENTS') }}
                </h3>
                <p class="text-xs text-n-slate-11 mt-0.5">
                  {{ $t('CRM.FUNNEL_SETTINGS.AGENTS_HINT') }}
                </p>
              </div>
            </div>
            <div class="p-5">
              <div class="space-y-1 max-h-48 overflow-y-auto">
                <label
                  v-for="agent in availableAgents"
                  :key="agent.id"
                  class="flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer hover:bg-n-alpha-2 transition-colors"
                >
                  <input
                    v-model="selectedAgents"
                    type="checkbox"
                    :value="agent.id"
                    class="w-4 h-4 rounded border-n-weak text-n-brand focus:ring-n-brand"
                  />
                  <div class="flex items-center gap-2">
                    <img
                      v-if="agent.avatar_url"
                      :src="agent.avatar_url"
                      :alt="agent.name"
                      class="w-7 h-7 rounded-full"
                    />
                    <div
                      v-else
                      class="w-7 h-7 rounded-full bg-n-brand flex items-center justify-center text-white text-xs font-medium"
                    >
                      {{ agent.name.charAt(0).toUpperCase() }}
                    </div>
                    <span class="text-sm text-n-slate-12">{{
                      agent.name
                    }}</span>
                  </div>
                </label>
              </div>
              <p
                v-if="selectedAgents.length > 0"
                class="text-xs text-n-teal-11 mt-3 font-medium"
              >
                {{ selectedAgents.length }}
                {{ $t('CRM.FUNNEL_SETTINGS.AGENTS_SELECTED') }}
              </p>
            </div>
          </div>
        </div>

        <div
          class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
        >
          <div class="flex items-center gap-3 px-5 py-4 border-b border-n-weak">
            <div
              class="w-8 h-8 rounded-lg bg-n-blue-3 flex items-center justify-center"
            >
              <span class="i-lucide-copy-check text-n-blue-11 text-base" />
            </div>
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('CRM.FUNNEL_SETTINGS.SECTIONS.DUPLICATES') }}
              </h3>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ $t('CRM.FUNNEL_SETTINGS.DUPLICATES.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="grid gap-4 p-5 md:grid-cols-2">
            <label
              class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
            >
              {{ $t('CRM.FUNNEL_SETTINGS.DUPLICATES.CONTACT_LABEL') }}
              <select
                v-model="duplicatePolicy"
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12"
              >
                <option value="one_open_per_contact">
                  {{ $t('CRM.FUNNEL_SETTINGS.DUPLICATES.ONE_OPEN') }}
                </option>
                <option value="allow_multiple">
                  {{ $t('CRM.FUNNEL_SETTINGS.DUPLICATES.ALLOW_MULTIPLE') }}
                </option>
              </select>
            </label>
            <label
              class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
            >
              {{ $t('CRM.FUNNEL_SETTINGS.DUPLICATES.CUSTOMER_LABEL') }}
              <select
                v-model="customerDuplicatePolicy"
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12"
              >
                <option value="inherit">
                  {{ $t('CRM.FUNNEL_SETTINGS.DUPLICATES.INHERIT') }}
                </option>
                <option value="allow_multiple">
                  {{ $t('CRM.FUNNEL_SETTINGS.DUPLICATES.ALLOW_CUSTOMER') }}
                </option>
                <option value="block">
                  {{ $t('CRM.FUNNEL_SETTINGS.DUPLICATES.BLOCK_CUSTOMER') }}
                </option>
              </select>
            </label>
          </div>
          <div
            class="flex items-center justify-between gap-4 px-5 py-4 border-t border-n-weak"
          >
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('CRM.FUNNEL_SETTINGS.DUPLICATES.ONE_CARD_LABEL') }}
              </p>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ $t('CRM.FUNNEL_SETTINGS.DUPLICATES.ONE_CARD_HELP') }}
              </p>
            </div>
            <Switch v-model="boardOneCardPerContact" />
          </div>
        </div>

        <!-- Movimento por fato: evento objetivo avança o card sem a IA -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
        >
          <div class="flex items-center gap-3 px-5 py-4 border-b border-n-weak">
            <div
              class="w-8 h-8 rounded-lg bg-n-teal-3 flex items-center justify-center"
            >
              <span class="i-lucide-calendar-check text-n-teal-11 text-base" />
            </div>
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('CRM.FUNNEL_SETTINGS.SECTIONS.STAGE_ON_EVENT') }}
              </h3>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ $t('CRM.FUNNEL_SETTINGS.STAGE_ON_EVENT.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="p-5">
            <div class="grid gap-4 md:grid-cols-2">
              <label
                v-for="event in STAGE_EVENT_KEYS"
                :key="event"
                class="flex flex-col gap-1.5 text-xs font-medium text-n-slate-11"
              >
                {{ $t(`CRM.FUNNEL_SETTINGS.STAGE_ON_EVENT.EVENTS.${event}`) }}
                <select
                  v-model="stageOnEvent[event]"
                  class="h-10 rounded-lg border border-n-weak bg-n-alpha-2 px-3 text-sm text-n-slate-12"
                >
                  <option value="">
                    {{ $t('CRM.FUNNEL_SETTINGS.STAGE_ON_EVENT.NONE') }}
                  </option>
                  <option
                    v-for="stage in orderedStages"
                    :key="stage.key"
                    :value="stage.key"
                  >
                    {{ stage.label || stage.key }}
                  </option>
                </select>
              </label>
            </div>
            <p class="text-xs text-n-slate-11 mt-4">
              {{ $t('CRM.FUNNEL_SETTINGS.STAGE_ON_EVENT.HINT') }}
              {{ $t('CRM.FUNNEL_SETTINGS.STAGE_ON_EVENT.BACKWARDS_NOTE') }}
            </p>
          </div>
        </div>

        <!-- Alerta de lead sem resposta (speed-to-lead) -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
        >
          <div class="flex items-center gap-3 px-5 py-4 border-b border-n-weak">
            <div
              class="w-8 h-8 rounded-lg bg-n-ruby-3 flex items-center justify-center"
            >
              <span class="i-lucide-bell-ring text-n-ruby-11 text-base" />
            </div>
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('CRM.FUNNEL_SETTINGS.SECTIONS.SPEED_TO_LEAD') }}
              </h3>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ $t('CRM.FUNNEL_SETTINGS.SPEED_TO_LEAD.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="p-5">
            <label
              class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11 mb-1.5"
            >
              <span class="i-lucide-clock text-xs" />
              {{ $t('CRM.FUNNEL_SETTINGS.SPEED_TO_LEAD.LABEL') }}
            </label>
            <woot-input
              v-model="speedToLeadMinutes"
              type="number"
              min="0"
              max="1440"
              class="w-32 mb-0"
              :placeholder="$t('CRM.FUNNEL_SETTINGS.SPEED_TO_LEAD.PLACEHOLDER')"
            />
            <p class="text-xs text-n-slate-11 mt-1.5">
              {{ $t('CRM.FUNNEL_SETTINGS.SPEED_TO_LEAD.HINT') }}
            </p>
          </div>
        </div>

        <!-- Alerta de lead parado (deal aging) -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
        >
          <div class="flex items-center gap-3 px-5 py-4 border-b border-n-weak">
            <div
              class="w-8 h-8 rounded-lg bg-n-amber-3 flex items-center justify-center"
            >
              <span class="i-lucide-hourglass text-n-amber-11 text-base" />
            </div>
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('CRM.FUNNEL_SETTINGS.SECTIONS.STALE_ALERT') }}
              </h3>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ $t('CRM.FUNNEL_SETTINGS.STALE_ALERT.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="p-5">
            <div class="flex items-center justify-between gap-3">
              <div>
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('CRM.FUNNEL_SETTINGS.STALE_ALERT.LABEL') }}
                </span>
                <p class="text-xs text-n-slate-11 mt-0.5">
                  {{ $t('CRM.FUNNEL_SETTINGS.STALE_ALERT.HINT') }}
                </p>
              </div>
              <Switch v-model="staleAlertEnabled" />
            </div>
            <div v-if="staleAlertEnabled" class="mt-4">
              <label
                class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11 mb-1.5"
              >
                <span class="i-lucide-calendar-clock text-xs" />
                {{ $t('CRM.FUNNEL_SETTINGS.STALE_ALERT.MAX_DAYS_LABEL') }}
              </label>
              <woot-input
                v-model="staleMaxDaysInStage"
                type="number"
                min="1"
                max="365"
                class="w-32 mb-0"
                :placeholder="
                  $t('CRM.FUNNEL_SETTINGS.STALE_ALERT.MAX_DAYS_PLACEHOLDER')
                "
              />
              <p class="text-xs text-n-slate-11 mt-1.5">
                {{ $t('CRM.FUNNEL_SETTINGS.STALE_ALERT.MAX_DAYS_HINT') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Follow-Up Config per Stage -->
        <div
          class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
        >
          <div class="flex items-center gap-3 px-5 py-4 border-b border-n-weak">
            <div
              class="w-8 h-8 rounded-lg bg-n-green-3 flex items-center justify-center"
            >
              <span class="i-lucide-repeat text-n-green-11 text-base" />
            </div>
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">
                {{ $t('CRM.FUNNEL_SETTINGS.SECTIONS.FOLLOW_UPS') }}
              </h3>
              <p class="text-xs text-n-slate-11 mt-0.5">
                {{ $t('CRM.FUNNEL_SETTINGS.FOLLOW_UPS.DESCRIPTION') }}
              </p>
            </div>
          </div>
          <div class="p-5 space-y-3">
            <div
              v-for="stage in orderedStages"
              :key="stage.key"
              class="border border-n-weak rounded-xl overflow-hidden"
            >
              <button
                type="button"
                class="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-n-alpha-2 transition-colors"
                @click="toggleStageFollowUp(stage.key)"
              >
                <div
                  class="w-3.5 h-3.5 rounded-full flex-shrink-0 ring-2 ring-white/20"
                  :style="{ backgroundColor: stage.color }"
                />
                <span class="flex-1 text-sm font-medium text-n-slate-12">
                  {{ stage.label }}
                </span>
                <span
                  v-if="getStageFollowUps(stage.key).length"
                  class="text-xs px-2.5 py-1 rounded-full bg-n-blue-3 text-n-blue-11 font-medium"
                >
                  {{ getStageFollowUps(stage.key).length }}
                  {{ $t('CRM.FUNNEL_SETTINGS.FOLLOW_UPS.STEPS') }}
                </span>
                <span
                  class="text-n-slate-10 transition-transform i-lucide-chevron-down"
                  :class="{ 'rotate-180': expandedStageFollowUp === stage.key }"
                />
              </button>
              <div
                v-if="expandedStageFollowUp === stage.key"
                class="border-t border-n-weak px-5 py-5 bg-n-alpha-1"
              >
                <FollowUpTimeline
                  :stage-key="stage.key"
                  :stage-label="stage.label"
                  :stage-color="stage.color"
                  :steps="getStageFollowUps(stage.key)"
                  :inboxes="availableInboxes"
                  :templates="followUpTemplates"
                  :templates-by-inbox="followUpTemplatesByInbox"
                  @update:steps="persistStageSteps(stage.key, $event)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Botao Salvar -->
        <div class="flex justify-end pt-4">
          <Button
            icon="i-lucide-save"
            :label="$t('CRM.FUNNEL_SETTINGS.SAVE')"
            :is-loading="isSaving"
            @click="saveSettings"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
